import { useTheme } from "../../../contexts/ThemeContext";

const DEEP_FEATURES = [
  {
    icon: "ri-calculator-line",
    title: "Profit Calculator",
    description:
      "The core of Trackly. Enter your buy price, sale price, platform, and shipping — and instantly see your true net profit after every fee. Built for speed. Accurate to the cent.",
    details: [
      "All platform fees pre-loaded and auto-applied",
      "Calculates payment processing, listing, and final value fees",
      "See your profit before you even list the item",
    ],
  },
  {
    icon: "ri-eye-off-line",
    title: "Hidden Fee Engine",
    description:
      "Most flippers only think about the hammer price. Trackly tracks every deduction: platform cut, payment processing, shipping cost, and any listing fees — nothing falls through the cracks.",
    details: [
      "Per-platform fee structures always up to date",
      "Shipping cost breakdown (what you charged vs. what you paid)",
      "Full cost basis including acquisition costs",
    ],
  },
  {
    icon: "ri-line-chart-line",
    title: "Daily Performance Dashboard",
    description:
      "Know how your reselling is actually going. Track profit, volume, ROI, and win rate day by day, week by week. See trends and understand whether your strategy is improving over time.",
    details: [
      "Daily, weekly, and monthly profit summaries",
      "ROI trend line across all your flips",
      "Best and worst performing categories",
    ],
  },
  {
    icon: "ri-history-line",
    title: "Flip History Log",
    description:
      "Every sale you run through Trackly is automatically saved to your history. Review past deals, identify patterns, and learn which items and platforms generate your best returns.",
    details: [
      "Full log of every sale with all cost details",
      "Filter by platform, item type, or date range",
      "Export your history as CSV anytime",
    ],
  },
];

export default function ProductFeatures() {
  const { isDark } = useTheme();

  return (
    <section id="features" className={`py-24 px-8 lg:px-16 transition-colors duration-300 ${isDark ? "bg-[#050505]" : "bg-white"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className={`font-inter text-xs uppercase tracking-[0.2em] block mb-4 ${isDark ? "text-[#666666]" : "text-[#888888]"}`}>
            What&apos;s Inside
          </span>
          <h2 className={`font-syne font-bold text-5xl lg:text-6xl ${isDark ? "text-white" : "text-[#111111]"}`}>
            Everything a reseller <br />
            <span className={isDark ? "text-[#444444]" : "text-[#cccccc]"}>actually needs.</span>
          </h2>
        </div>

        <div className="flex flex-col gap-6">
          {DEEP_FEATURES.map((feat, idx) => (
            <div
              key={feat.title}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-start border rounded-2xl p-8 lg:p-12 transition-colors duration-300 cursor-default ${
                isDark
                  ? "bg-[#0a0a0a] border-white/5 hover:border-white/8"
                  : "bg-[#f8f8f8] border-black/5 hover:border-black/8"
              }`}
            >
              <div className={idx % 2 === 1 ? "lg:order-2" : "lg:order-1"}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${isDark ? "bg-white/5" : "bg-black/5"}`}>
                    <i className={`${feat.icon} text-lg ${isDark ? "text-[#666666]" : "text-[#888888]"}`} />
                  </div>
                  <h3 className={`font-syne font-semibold text-xl ${isDark ? "text-white" : "text-[#111111]"}`}>{feat.title}</h3>
                </div>
                <p className={`font-inter text-sm leading-relaxed mb-6 ${isDark ? "text-[#555555]" : "text-[#666666]"}`}>
                  {feat.description}
                </p>
                <ul className="flex flex-col gap-2.5">
                  {feat.details.map((d) => (
                    <li key={d} className="flex items-start gap-2.5">
                      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`ri-check-line text-sm ${isDark ? "text-[#666666]" : "text-[#888888]"}`} />
                      </div>
                      <span className={`font-inter text-sm ${isDark ? "text-[#666666]" : "text-[#777777]"}`}>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Mock UI Visual */}
              <div className={idx % 2 === 1 ? "lg:order-1" : "lg:order-2"}>
                <div className={`border rounded-xl p-5 ${isDark ? "bg-[#0d0d0d] border-white/8" : "bg-white border-black/8"}`}>
                  {idx === 0 && (
                    <div className="flex flex-col gap-3">
                      <div className="grid grid-cols-2 gap-2">
                        {[["Buy Price", "$45.00"], ["Sale Price", "$120.00"], ["Platform", "eBay"], ["Shipping", "$8.50"]].map(([l, v]) => (
                          <div key={l} className={`border rounded-lg p-3 ${isDark ? "bg-[#111111] border-white/5" : "bg-[#f5f5f5] border-black/5"}`}>
                            <p className={`font-inter text-xs ${isDark ? "text-[#555555]" : "text-[#888888]"}`}>{l}</p>
                            <p className={`font-inter text-sm ${isDark ? "text-[#888888]" : "text-[#444444]"}`}>{v}</p>
                          </div>
                        ))}
                      </div>
                      <div className={`border rounded-lg p-3.5 flex items-center justify-between ${isDark ? "bg-white/4 border-white/8" : "bg-black/4 border-black/8"}`}>
                        <span className={`font-inter text-sm ${isDark ? "text-[#666666]" : "text-[#777777]"}`}>Net Profit</span>
                        <span className={`font-syne font-bold text-xl ${isDark ? "text-white" : "text-[#111111]"}`}>$47.03</span>
                      </div>
                    </div>
                  )}
                  {idx === 1 && (
                    <div className="flex flex-col gap-2.5">
                      <p className={`font-inter text-xs uppercase tracking-widest mb-1 ${isDark ? "text-[#555555]" : "text-[#888888]"}`}>Fee Breakdown</p>
                      {[
                        ["eBay Final Value Fee", "13.25%", "$15.90"],
                        ["Payment Processing", "2.9% + $0.30", "$3.81"],
                        ["Shipping (your cost)", "—", "$8.50"],
                        ["Listing Fee", "Free (250/mo)", "$0.00"],
                      ].map(([name, rate, amount]) => (
                        <div key={name} className={`flex items-center justify-between py-2 border-b ${isDark ? "border-white/4" : "border-black/4"}`}>
                          <div>
                            <p className={`font-inter text-xs ${isDark ? "text-[#555555]" : "text-[#777777]"}`}>{name}</p>
                            <p className={`font-inter text-xs ${isDark ? "text-[#444444]" : "text-[#aaaaaa]"}`}>{rate}</p>
                          </div>
                          <span className={`font-inter text-xs ${isDark ? "text-[#666666]" : "text-[#888888]"}`}>{amount}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between pt-2">
                        <span className={`font-inter text-xs ${isDark ? "text-[#666666]" : "text-[#777777]"}`}>Total Fees</span>
                        <span className={`font-inter text-sm ${isDark ? "text-white" : "text-[#111111]"}`}>$28.21</span>
                      </div>
                    </div>
                  )}
                  {idx === 2 && (
                    <div className="flex flex-col gap-3">
                      <div className="grid grid-cols-3 gap-2 mb-1">
                        {["Week", "Month", "Year"].map((t, i) => (
                          <div key={t} className={`text-center py-2 rounded-lg font-inter text-xs cursor-pointer transition-colors ${i === 1 ? (isDark ? "bg-white/8 text-white" : "bg-black/8 text-[#111111]") : (isDark ? "text-[#444444]" : "text-[#aaaaaa]")}`}>{t}</div>
                        ))}
                      </div>
                      {[
                        { label: "Net Profit", value: "$623.40", sub: "18 flips" },
                        { label: "Avg. ROI", value: "87.2%", sub: "per flip" },
                        { label: "Total Fees Paid", value: "$142.80", sub: "18.6% of sales" },
                        { label: "Best Flip", value: "+$198.40", sub: "Jordan RC" },
                      ].map((s) => (
                        <div key={s.label} className={`flex items-center justify-between py-2 border-b ${isDark ? "border-white/4" : "border-black/4"}`}>
                          <div>
                            <p className={`font-inter text-xs ${isDark ? "text-[#555555]" : "text-[#777777]"}`}>{s.label}</p>
                            <p className={`font-inter text-xs ${isDark ? "text-[#444444]" : "text-[#aaaaaa]"}`}>{s.sub}</p>
                          </div>
                          <span className={`font-inter text-sm ${isDark ? "text-white" : "text-[#111111]"}`}>{s.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {idx === 3 && (
                    <div className="flex flex-col gap-2.5">
                      <p className={`font-inter text-xs uppercase tracking-widest mb-1 ${isDark ? "text-[#555555]" : "text-[#888888]"}`}>Recent Flips</p>
                      {[
                        { card: "Ohtani PSA 10 Topps", profit: "+$39.20", roi: "103%" },
                        { card: "MJ 86 Fleer RC", profit: "+$198.40", roi: "90%" },
                        { card: "Mahomes Silver Prizm", profit: "–$4.50", roi: "–7%", loss: true },
                        { card: "Mantle 52 Reprint", profit: "+$10.80", roi: "90%" },
                      ].map((f) => (
                        <div key={f.card} className={`flex items-center justify-between py-2.5 border-b ${isDark ? "border-white/4" : "border-black/4"}`}>
                          <span className={`font-inter text-xs truncate pr-4 max-w-[180px] ${isDark ? "text-[#555555]" : "text-[#777777]"}`}>{f.card}</span>
                          <div className="text-right flex-shrink-0">
                            <p className={`font-inter text-xs ${f.loss ? (isDark ? "text-[#555555]" : "text-[#aaaaaa]") : (isDark ? "text-[#888888]" : "text-[#555555]")}`}>{f.profit}</p>
                            <p className={`font-inter text-xs ${isDark ? "text-[#444444]" : "text-[#aaaaaa]"}`}>{f.roi} ROI</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
